/**
 * The new-message chime: two soft sine notes made with Web Audio, so there is
 * no sound file to load and nothing plays before the person has used the page
 * (browsers keep audio locked until a click or key press; then it resumes).
 * Quiet on purpose: it should be noticed, not startle.
 */
let ctx: AudioContext | null = null;
let lastPlayed = 0;

/** No more than one chime per this many ms, however many messages arrive at once. */
const MIN_GAP_MS = 1500;

function audioContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor: typeof AudioContext | undefined =
    typeof window === 'undefined' ? undefined : window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

function note(ac: AudioContext, freq: number, start: number, length: number): void {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.12, start + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + length);
  osc.connect(gain).connect(ac.destination);
  osc.start(start);
  osc.stop(start + length + 0.02);
}

/** Plays the chime; does nothing if audio is unavailable or it just played. */
export function playMessageChime(now: number = Date.now()): boolean {
  if (now - lastPlayed < MIN_GAP_MS) return false;
  const ac = audioContext();
  if (!ac) return false;
  lastPlayed = now;
  if (ac.state === 'suspended') void ac.resume().catch(() => undefined);
  const t = ac.currentTime + 0.01;
  note(ac, 880, t, 0.18);
  note(ac, 1318.5, t + 0.12, 0.26);
  return true;
}
